import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import * as pdfjsLib from 'pdfjs-dist';
import Layout from '../../components/Layout';
import { renderDxfToImage } from '../../utils/dxfRenderer';
import { renderIfcToImage } from '../../utils/ifcRenderer';
import IfcViewer3D from '../../components/IfcViewer3D';
import {
  Upload, Trash2, Loader2, MapPin, FileText, Ruler, Boxes,
  ZoomIn, ZoomOut, Maximize, X, Info, FileDown, ChevronLeft, ChevronRight, Box, Layers
} from 'lucide-react';
import { toast } from 'sonner';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const jobLabel = (job) => {
  const r = job.result || {};
  if (job.type === 'MEASURE_2D') return `${r.area ?? '-'} ${r.unit || 'm'}² area`;
  if (job.type === 'MEASURE_3D') return `${r.surfaceArea ?? r.area ?? '-'} surface`;
  if (job.type === 'OBJECT_INTEL') {
    const count = r.count ?? r.detectedCount ?? r.totalCount;
    return count != null ? `${count} objects` : 'Object analysis';
  }
  return job.type;
};

const jobActual = (job) => {
  const r = job.result || {};
  if (job.type === 'OBJECT_INTEL') return r.count ?? r.detectedCount ?? r.totalCount ?? null;
  if (job.type === 'MEASURE_2D') return r.area ?? null;
  if (job.type === 'MEASURE_3D') return r.surfaceArea ?? r.area ?? null;
  return null;
};

const jobIcon = (type) => {
  if (type === 'OBJECT_INTEL') return <Boxes className="w-4 h-4" />;
  return <Ruler className="w-4 h-4" />;
};

const ReferenceDrawing = () => {
  const { projectId } = useParams();

  const [drawings, setDrawings] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [drawingImg, setDrawingImg] = useState(null); // {dataUrl, width, height}
  const [loadingDrawing, setLoadingDrawing] = useState(false);
  const [drawingError, setDrawingError] = useState(null);
  const [numPages, setNumPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const pdfDocRef = useRef(null);

  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const [selectedPin, setSelectedPin] = useState(null);
  const [plannedInput, setPlannedInput] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);
  const [jobQuery, setJobQuery] = useState('');
  const [jobTypeFilter, setJobTypeFilter] = useState('all'); // all | measure | object | pinned
  const [ifcViewMode, setIfcViewMode] = useState('2d'); // '2d' snapshot | '3d' interactive
  const [ifcArrayBuffer, setIfcArrayBuffer] = useState(null);
  const [active3DJobId, setActive3DJobId] = useState(null);

  const viewerRef = useRef(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const panState = useRef(null);

  const selectedDrawing = drawings.find((d) => d.id === selectedId) || null;
  const currentDrawingPage = selectedDrawing && selectedDrawing.fileType === 'pdf' ? currentPage : 1;
  const pinnedJobs = jobs.filter(
    (j) =>
      j.drawingLocation &&
      j.drawingLocation.drawingId === selectedId &&
      j.drawingLocation.mode !== '3d' &&
      (j.drawingLocation.page || 1) === currentDrawingPage
  );

  const pins3D = jobs.filter(
    (j) => j.drawingLocation && j.drawingLocation.drawingId === selectedId && j.drawingLocation.mode === '3d'
  );

  const filteredJobs = jobs.filter((j) => {
    const label = (j.roomName || (j.result && j.result.roomName) || j.type || '').toLowerCase();
    if (jobQuery && !label.includes(jobQuery.toLowerCase()) && !(j.type || '').toLowerCase().includes(jobQuery.toLowerCase())) {
      return false;
    }
    if (jobTypeFilter === 'measure') return j.type === 'MEASURE_2D' || j.type === 'MEASURE_3D';
    if (jobTypeFilter === 'object') return j.type === 'OBJECT_INTEL';
    if (jobTypeFilter === 'pinned') return j.drawingLocation && j.drawingLocation.drawingId === selectedId;
    return true;
  });

  const fetchDrawings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/reference-drawings`);
      setDrawings(res.data);
      if (res.data.length && !selectedId) setSelectedId(res.data[0].id);
    } catch (e) {
      toast.error('Failed to load drawings');
    }
  }, [projectId, selectedId]);

  const fetchJobs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/projects/${projectId}/analysis-jobs`);
      setJobs(res.data || []);
    } catch (e) {
      // non-fatal
    }
  }, [projectId]);

  useEffect(() => {
    fetchDrawings();
    fetchJobs();
  }, [fetchDrawings, fetchJobs]);

  const renderPdfPage = useCallback(async (pageNum) => {
    if (!pdfDocRef.current) return;
    const page = await pdfDocRef.current.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    setDrawingImg({ dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height });
  }, []);

  const goToPage = async (n) => {
    const clamped = Math.min(numPages, Math.max(1, n));
    setCurrentPage(clamped);
    setSelectedPin(null);
    await renderPdfPage(clamped);
  };

  // ---- Load & rasterize the selected drawing ----
  useEffect(() => {
    if (!selectedDrawing) {
      setDrawingImg(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingDrawing(true);
      setDrawingError(null);
      setDrawingImg(null);
      setNumPages(1);
      setCurrentPage(1);
      setIfcArrayBuffer(null);
      setIfcViewMode('2d');
      setActive3DJobId(null);
      pdfDocRef.current = null;
      try {
        const resp = await axios.get(`${API}/reference-drawings/${selectedDrawing.id}/file`, {
          responseType: 'arraybuffer',
        });
        if (selectedDrawing.fileType === 'pdf') {
          const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(resp.data) }).promise;
          if (cancelled) return;
          pdfDocRef.current = pdf;
          setNumPages(pdf.numPages);
          setCurrentPage(1);
          await renderPdfPage(1);
        } else if (selectedDrawing.fileType === 'ifc') {
          if (!cancelled) setIfcArrayBuffer(resp.data.slice(0));
          const img = await renderIfcToImage(resp.data);
          if (!cancelled) setDrawingImg(img);
        } else {
          const text = new TextDecoder().decode(resp.data);
          const img = renderDxfToImage(text);
          if (!cancelled) setDrawingImg(img);
        }
      } catch (e) {
        console.error('Drawing render failed:', e);
        if (!cancelled) setDrawingError('Could not render this drawing. The file may be unsupported or corrupt.');
      } finally {
        if (!cancelled) setLoadingDrawing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedDrawing, renderPdfPage]);

  // ---- Fit to viewer when a new image loads ----
  const fitToView = useCallback(() => {
    if (!drawingImg || !viewerRef.current) return;
    const vw = viewerRef.current.clientWidth;
    const vh = viewerRef.current.clientHeight;
    const scale = Math.min(vw / drawingImg.width, vh / drawingImg.height) * 0.95;
    const tx = (vw - drawingImg.width * scale) / 2;
    const ty = (vh - drawingImg.height * scale) / 2;
    setView({ scale, tx, ty });
  }, [drawingImg]);

  useEffect(() => {
    fitToView();
  }, [drawingImg, fitToView]);

  // ---- Pan ----
  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    panState.current = { startX: e.clientX, startY: e.clientY, tx: view.tx, ty: view.ty };
  };
  const onMouseMove = (e) => {
    if (!panState.current) return;
    const dx = e.clientX - panState.current.startX;
    const dy = e.clientY - panState.current.startY;
    setView((v) => ({ ...v, tx: panState.current.tx + dx, ty: panState.current.ty + dy }));
  };
  const onMouseUp = () => {
    panState.current = null;
  };

  // ---- Zoom (cursor-anchored) ----
  const onWheel = (e) => {
    e.preventDefault();
    const rect = viewerRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setView((v) => {
      const newScale = Math.min(20, Math.max(0.05, v.scale * factor));
      const k = newScale / v.scale;
      return {
        scale: newScale,
        tx: cx - (cx - v.tx) * k,
        ty: cy - (cy - v.ty) * k,
      };
    });
  };

  const zoomButton = (dir) => {
    setView((v) => {
      const factor = dir > 0 ? 1.2 : 1 / 1.2;
      return { ...v, scale: Math.min(20, Math.max(0.05, v.scale * factor)) };
    });
  };

  // ---- Upload ----
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'dxf', 'ifc'].includes(ext)) {
      toast.error('Only PDF, DXF and IFC files are supported');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/projects/${projectId}/reference-drawings`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Reference drawing uploaded');
      await fetchDrawings();
      setSelectedId(res.data.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reference drawing? Pinned jobs will be unpinned.')) return;
    try {
      await axios.delete(`${API}/reference-drawings/${id}`);
      toast.success('Drawing deleted');
      if (selectedId === id) setSelectedId(null);
      await fetchDrawings();
      await fetchJobs();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // ---- Pin / unpin ----
  const pinJobAt = async (jobId, nx, ny) => {
    try {
      const res = await axios.patch(`${API}/analysis-jobs/${jobId}/drawing-location`, {
        drawingId: selectedId,
        x: nx,
        y: ny,
        page: currentDrawingPage,
      });
      setJobs((prev) =>
        prev.map((j) => (j.id === jobId ? { ...j, drawingLocation: res.data.drawingLocation } : j))
      );
    } catch (e) {
      toast.error('Failed to pin job');
    }
  };

  const clearPlanned = async (job) => {
    try {
      const res = await axios.patch(`${API}/analysis-jobs/${job.id}/drawing-location`, {
        drawingId: selectedId,
        x: job.drawingLocation.x,
        y: job.drawingLocation.y,
        page: job.drawingLocation.page || currentDrawingPage,
        clearPlanned: true,
      });
      setJobs((prev) =>
        prev.map((j) => (j.id === job.id ? { ...j, drawingLocation: res.data.drawingLocation } : j))
      );
      setPlannedInput('');
      toast.success('Planned quantity cleared');
    } catch (e) {
      toast.error('Failed to clear planned quantity');
    }
  };

  const unpinJob = async (jobId) => {
    try {
      await axios.patch(`${API}/analysis-jobs/${jobId}/drawing-location`, { drawingId: null });
      setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, drawingLocation: null } : j)));
      setSelectedPin(null);
    } catch (e) {
      toast.error('Failed to unpin');
    }
  };

  const pinJob3D = async (jobId, pin3D) => {
    try {
      const res = await axios.patch(`${API}/analysis-jobs/${jobId}/drawing-location`, {
        drawingId: selectedId,
        mode: '3d',
        pin3D,
      });
      setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, drawingLocation: res.data.drawingLocation } : j)));
      setActive3DJobId(null);
      toast.success('Pinned in 3D');
    } catch (e) {
      toast.error('Failed to place 3D pin');
    }
  };

  const savePlanned = async (job) => {
    const val = plannedInput === '' ? null : parseFloat(plannedInput);
    try {
      const res = await axios.patch(`${API}/analysis-jobs/${job.id}/drawing-location`, {
        drawingId: selectedId,
        x: job.drawingLocation.x,
        y: job.drawingLocation.y,
        page: job.drawingLocation.page || currentDrawingPage,
        plannedQuantity: val,
      });
      setJobs((prev) =>
        prev.map((j) => (j.id === job.id ? { ...j, drawingLocation: res.data.drawingLocation } : j))
      );
      toast.success('Planned quantity saved');
    } catch (e) {
      toast.error('Failed to save planned quantity');
    }
  };

  const renderPdfPageToImage = async (pageNum) => {
    const page = await pdfDocRef.current.getPage(pageNum);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    return { dataUrl: canvas.toDataURL('image/png'), width: canvas.width, height: canvas.height };
  };

  const composeAnnotated = async (imgObj = drawingImg, jobsList = pinnedJobs) => {
    const canvas = document.createElement('canvas');
    canvas.width = imgObj.width;
    canvas.height = imgObj.height;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = rej;
      img.src = imgObj.dataUrl;
    });
    ctx.drawImage(img, 0, 0);
    const pinsSummary = [];
    jobsList.forEach((job) => {
      const x = job.drawingLocation.x * canvas.width;
      const y = job.drawingLocation.y * canvas.height;
      ctx.fillStyle = '#16a34a';
      ctx.beginPath();
      ctx.arc(x, y, 10, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();
      const label = job.roomName || job.result?.roomName || job.type;
      ctx.font = 'bold 22px sans-serif';
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = 'rgba(255,255,255,0.88)';
      ctx.fillRect(x + 12, y - 18, tw + 10, 28);
      ctx.fillStyle = '#0f172a';
      ctx.fillText(label, x + 16, y + 3);
      const actual = jobActual(job);
      const planned = job.drawingLocation.plannedQuantity ?? null;
      const variance = planned != null && actual != null ? Math.round((actual - planned) * 100) / 100 : null;
      pinsSummary.push({ label, type: job.type, actual: actual != null ? actual : '-', planned, variance });
    });
    return { annotated: canvas.toDataURL('image/png'), pinsSummary };
  };

  const triggerDownload = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const buildDrawingPages = async () => {
    const allPinned = jobs.filter((j) => j.drawingLocation && j.drawingLocation.drawingId === selectedId);
    if (selectedDrawing.fileType === 'pdf' && numPages > 1) {
      const byPage = {};
      allPinned.forEach((j) => {
        const p = j.drawingLocation.page || 1;
        (byPage[p] = byPage[p] || []).push(j);
      });
      const pageNums = Object.keys(byPage).map(Number).sort((a, b) => a - b);
      const pages = [];
      if (pageNums.length === 0) {
        const { annotated, pinsSummary } = await composeAnnotated(drawingImg, []);
        pages.push({ annotatedImage: annotated, pins: pinsSummary, label: `Page ${currentPage}` });
      } else {
        for (const pn of pageNums) {
          const pageImg = pn === currentPage ? drawingImg : await renderPdfPageToImage(pn);
          const { annotated, pinsSummary } = await composeAnnotated(pageImg, byPage[pn]);
          pages.push({ annotatedImage: annotated, pins: pinsSummary, label: `Page ${pn}` });
        }
      }
      return pages;
    }
    const { annotated, pinsSummary } = await composeAnnotated(drawingImg, pinnedJobs);
    return [{ annotatedImage: annotated, pins: pinsSummary, label: selectedDrawing.fileName }];
  };

  const downloadProjectReport = async () => {
    setGeneratingReport(true);
    try {
      const drawingsPayload = [];
      if (drawingImg && selectedDrawing) {
        const pages = await buildDrawingPages();
        pages.forEach((pg) => {
          drawingsPayload.push({
            drawingId: selectedDrawing.id,
            drawingName: `${selectedDrawing.fileName}${pages.length > 1 ? ` — ${pg.label}` : ''}`,
            annotatedImage: pg.annotatedImage,
            pins: pg.pins,
          });
        });
      }
      const res = await axios.post(
        `${API}/projects/${projectId}/report`,
        { drawings: drawingsPayload },
        { responseType: 'blob' }
      );
      triggerDownload(new Blob([res.data], { type: 'application/pdf' }), 'project_report.pdf');
      toast.success('Project report downloaded');
    } catch (e) {
      toast.error('Failed to generate project report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const downloadPlanReport = async () => {
    if (!drawingImg || !selectedDrawing) return;
    setGeneratingReport(true);
    try {
      const pages = await buildDrawingPages();
      const payload = pages.length > 1
        ? { pages }
        : { annotatedImage: pages[0].annotatedImage, pins: pages[0].pins };
      const res = await axios.post(
        `${API}/projects/${projectId}/reference-drawings/${selectedDrawing.id}/report`,
        payload,
        { responseType: 'blob' }
      );
      triggerDownload(new Blob([res.data], { type: 'application/pdf' }), `plan_report_${selectedDrawing.fileName || 'drawing'}.pdf`);
      toast.success('Plan report downloaded');
    } catch (e) {
      toast.error('Failed to generate plan report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const computeNormalized = (clientX, clientY) => {
    const rect = imgRef.current.getBoundingClientRect();
    const nx = (clientX - rect.left) / rect.width;
    const ny = (clientY - rect.top) / rect.height;
    return { nx: Math.min(1, Math.max(0, nx)), ny: Math.min(1, Math.max(0, ny)) };
  };

  const onDrop = (e) => {
    e.preventDefault();
    const jobId = e.dataTransfer.getData('text/jobId');
    if (!jobId || !imgRef.current || !selectedId) return;
    const { nx, ny } = computeNormalized(e.clientX, e.clientY);
    pinJobAt(jobId, nx, ny);
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900">Reference Drawing</h1>
          <p className="text-slate-600 text-sm">
            Upload a floor plan (PDF or DXF) and drag measurement / object-intelligence jobs onto it to pin their location.
          </p>
        </div>

        <div className="flex gap-4" style={{ height: 'calc(100vh - 190px)' }}>
          {/* Sidebar */}
          <div className="w-80 flex-shrink-0 flex flex-col bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.dxf,.ifc"
                onChange={handleUpload}
                className="hidden"
                data-testid="drawing-file-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60"
                data-testid="upload-drawing-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? 'Uploading…' : 'Upload Drawing'}
              </button>
              <p className="text-[11px] text-slate-400 mt-2">PDF (full) · DXF & IFC (preview, Beta)</p>
            </div>

            {/* Drawings list */}
            <div className="p-3 border-b border-slate-200 max-h-40 overflow-y-auto">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Drawings</p>
              {drawings.length === 0 && <p className="text-xs text-slate-400">No drawings yet.</p>}
              {drawings.map((d) => (
                <div
                  key={d.id}
                  onClick={() => setSelectedId(d.id)}
                  className={`flex items-center justify-between px-2 py-2 rounded-lg cursor-pointer mb-1 ${
                    selectedId === d.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50'
                  }`}
                  data-testid="drawing-item"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <span className="text-sm truncate">{d.fileName}</span>
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{d.fileType}</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(d.id); }}
                    className="p-1 text-slate-400 hover:text-red-500"
                    data-testid="delete-drawing-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Jobs list (draggable) */}
            <div className="flex-1 overflow-y-auto p-3">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">
                Jobs — drag onto the plan
              </p>
              <input
                type="text"
                value={jobQuery}
                onChange={(e) => setJobQuery(e.target.value)}
                placeholder="Search jobs…"
                className="w-full px-2 py-1.5 mb-2 border border-slate-200 rounded-lg text-sm"
                data-testid="job-search-input"
              />
              <div className="flex gap-1 mb-2 flex-wrap">
                {[['all', 'All'], ['measure', 'Measure'], ['object', 'Objects'], ['pinned', 'Pinned']].map(([val, lbl]) => (
                  <button
                    key={val}
                    onClick={() => setJobTypeFilter(val)}
                    className={`px-2 py-1 rounded-full text-xs ${jobTypeFilter === val ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}
                    data-testid={`job-filter-${val}`}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
              {filteredJobs.length === 0 && <p className="text-xs text-slate-400">No matching jobs.</p>}
              {filteredJobs.map((job) => {
                const pinnedHere = job.drawingLocation && job.drawingLocation.drawingId === selectedId;
                return (
                  <div
                    key={job.id}
                    draggable={!!selectedId}
                    onDragStart={(e) => e.dataTransfer.setData('text/jobId', job.id)}
                    className={`flex items-center gap-2 px-2 py-2 rounded-lg mb-1 border text-sm ${
                      pinnedHere ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'
                    } ${selectedId ? 'cursor-grab active:cursor-grabbing' : 'opacity-60'}`}
                    data-testid="job-item"
                  >
                    <span className="text-slate-500">{jobIcon(job.type)}</span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium text-slate-800">
                        {job.roomName || job.result?.roomName || job.type}
                      </p>
                      <p className="truncate text-xs text-slate-500">{jobLabel(job)}</p>
                    </div>
                    {pinnedHere && <MapPin className="w-4 h-4 text-green-600" />}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Viewer */}
          <div className="flex-1 relative bg-slate-100 rounded-xl border border-slate-200 overflow-hidden">
            {/* Controls */}
            <div className="absolute top-3 right-3 z-20 flex gap-2">
              {selectedDrawing?.fileType === 'ifc' && (
                <div className="flex rounded-lg overflow-hidden border border-slate-200 shadow" data-testid="ifc-mode-toggle">
                  <button onClick={() => setIfcViewMode('2d')} className={`px-2.5 py-2 text-sm flex items-center gap-1 ${ifcViewMode === '2d' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="ifc-mode-2d">
                    <Layers className="w-4 h-4" /> 2D
                  </button>
                  <button onClick={() => setIfcViewMode('3d')} className={`px-2.5 py-2 text-sm flex items-center gap-1 ${ifcViewMode === '3d' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid="ifc-mode-3d">
                    <Box className="w-4 h-4" /> 3D
                  </button>
                </div>
              )}
              <button onClick={downloadProjectReport} disabled={generatingReport} className="px-3 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1 text-sm" data-testid="project-report-btn">
                {generatingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Project Report
              </button>
              {!(selectedDrawing?.fileType === 'ifc' && ifcViewMode === '3d') && (
                <>
                  <button onClick={downloadPlanReport} disabled={!drawingImg || generatingReport} className="px-3 py-2 bg-teal-600 text-white rounded-lg shadow hover:bg-teal-700 disabled:opacity-50 flex items-center gap-1 text-sm" data-testid="plan-report-btn">
                    {generatingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Plan Report
                  </button>
                  <button onClick={() => zoomButton(1)} className="p-2 bg-white rounded-lg shadow border border-slate-200 hover:bg-slate-50" data-testid="zoom-in"><ZoomIn className="w-4 h-4" /></button>
                  <button onClick={() => zoomButton(-1)} className="p-2 bg-white rounded-lg shadow border border-slate-200 hover:bg-slate-50" data-testid="zoom-out"><ZoomOut className="w-4 h-4" /></button>
                  <button onClick={fitToView} className="p-2 bg-white rounded-lg shadow border border-slate-200 hover:bg-slate-50" data-testid="fit-view"><Maximize className="w-4 h-4" /></button>
                </>
              )}
            </div>

            {/* Interactive 3D IFC overlay */}
            {selectedDrawing?.fileType === 'ifc' && ifcViewMode === '3d' && (
              <div className="absolute inset-0 z-10 bg-slate-100" data-testid="ifc-3d-viewer-wrap">
                {ifcArrayBuffer ? (
                  <IfcViewer3D
                    arrayBuffer={ifcArrayBuffer}
                    jobs={jobs}
                    pins3D={pins3D}
                    activeJobId={active3DJobId}
                    onSelectActiveJob={setActive3DJobId}
                    onPin={pinJob3D}
                    onUnpin={unpinJob}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Preparing 3D…</div>
                )}
              </div>
            )}

            {/* Multi-page PDF navigation */}
            {selectedDrawing && selectedDrawing.fileType === 'pdf' && numPages > 1 && (
              <div className="absolute top-3 left-3 z-20 flex items-center gap-2 bg-white rounded-lg shadow border border-slate-200 px-2 py-1" data-testid="page-nav">
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1} className="p-1 rounded hover:bg-slate-100 disabled:opacity-40" data-testid="page-prev"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-xs font-medium text-slate-700" data-testid="page-indicator">Page {currentPage} / {numPages}</span>
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= numPages} className="p-1 rounded hover:bg-slate-100 disabled:opacity-40" data-testid="page-next"><ChevronRight className="w-4 h-4" /></button>
              </div>
            )}

            <div
              ref={viewerRef}
              className="w-full h-full overflow-hidden cursor-move"
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              onWheel={onWheel}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              data-testid="drawing-viewer"
            >
              {!selectedDrawing && (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                  <FileText className="w-12 h-12 mb-3" />
                  <p>Upload or select a reference drawing to begin.</p>
                </div>
              )}

              {loadingDrawing && (
                <div className="w-full h-full flex items-center justify-center text-slate-500">
                  <Loader2 className="w-6 h-6 animate-spin mr-2" /> Rendering drawing…
                </div>
              )}

              {drawingError && !loadingDrawing && (
                <div className="w-full h-full flex flex-col items-center justify-center text-red-500 px-6 text-center">
                  <Info className="w-8 h-8 mb-2" />
                  {drawingError}
                </div>
              )}

              {drawingImg && !loadingDrawing && (
                <div
                  style={{
                    transform: `translate(${view.tx}px, ${view.ty}px) scale(${view.scale})`,
                    transformOrigin: '0 0',
                    width: drawingImg.width,
                    height: drawingImg.height,
                    position: 'absolute',
                  }}
                >
                  <img
                    ref={imgRef}
                    src={drawingImg.dataUrl}
                    width={drawingImg.width}
                    height={drawingImg.height}
                    alt="Reference drawing"
                    draggable={false}
                    className="select-none pointer-events-none block"
                  />

                  {/* Pins */}
                  {pinnedJobs.map((job) => (
                    <div
                      key={job.id}
                      style={{
                        position: 'absolute',
                        left: job.drawingLocation.x * drawingImg.width,
                        top: job.drawingLocation.y * drawingImg.height,
                        transform: `translate(-50%, -100%) scale(${1 / view.scale})`,
                        transformOrigin: 'bottom center',
                      }}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData('text/jobId', job.id)}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedPin === job.id) {
                          setSelectedPin(null);
                        } else {
                          setSelectedPin(job.id);
                          setPlannedInput(
                            job.drawingLocation.plannedQuantity != null
                              ? String(job.drawingLocation.plannedQuantity)
                              : ''
                          );
                        }
                      }}
                      className="cursor-pointer"
                      data-testid="job-pin"
                    >
                      <div className="flex flex-col items-center">
                        <div className="bg-green-600 text-white text-xs px-2 py-1 rounded-lg shadow-lg whitespace-nowrap flex items-center gap-1">
                          {jobIcon(job.type)}
                          <span>{job.roomName || job.result?.roomName || job.type}</span>
                          {(() => {
                            const actual = jobActual(job);
                            const planned = job.drawingLocation.plannedQuantity;
                            if (planned == null || actual == null) return null;
                            const v = Math.round((actual - planned) * 100) / 100;
                            return (
                              <span className={`ml-1 px-1 rounded ${v === 0 ? 'bg-white/25' : v > 0 ? 'bg-amber-400 text-slate-900' : 'bg-red-500'}`} data-testid="pin-variance-chip">
                                {v > 0 ? '+' : ''}{v}
                              </span>
                            );
                          })()}
                        </div>
                        <MapPin className="w-6 h-6 text-green-600 -mt-0.5" fill="#16a34a" />

                        {selectedPin === job.id && (
                          <div
                            className="absolute top-full mt-1 w-52 bg-white rounded-lg shadow-xl border border-slate-200 p-3 text-left z-30"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex justify-between items-start mb-1">
                              <p className="font-semibold text-slate-800 text-sm">
                                {job.roomName || job.result?.roomName || job.type}
                              </p>
                              <button onClick={() => setSelectedPin(null)} className="text-slate-400 hover:text-slate-600">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-xs text-slate-500 mb-1">Type: {job.type}</p>
                            <p className="text-xs text-slate-600 mb-2">{jobLabel(job)}</p>

                            {/* Planned vs Actual */}
                            {(() => {
                              const actual = jobActual(job);
                              const planned = job.drawingLocation.plannedQuantity;
                              const variance = planned != null && actual != null
                                ? Math.round((actual - planned) * 100) / 100
                                : null;
                              return (
                                <div className="mb-2 border-t border-slate-100 pt-2">
                                  <p className="text-xs text-slate-500">
                                    Actual: <span className="font-semibold text-slate-700">{actual != null ? actual : '-'}</span>
                                  </p>
                                  <div className="flex items-center gap-1 mt-1">
                                    <input
                                      type="number"
                                      value={plannedInput}
                                      onChange={(e) => setPlannedInput(e.target.value)}
                                      placeholder="Planned"
                                      className="w-20 px-2 py-1 border border-slate-300 rounded text-xs"
                                      data-testid="planned-input"
                                    />
                                    <button
                                      onClick={() => savePlanned(job)}
                                      className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                                      data-testid="save-planned-btn"
                                    >
                                      Save
                                    </button>
                                    {planned != null && (
                                      <button
                                        onClick={() => clearPlanned(job)}
                                        className="px-2 py-1 bg-slate-200 text-slate-700 rounded text-xs hover:bg-slate-300"
                                        data-testid="clear-planned-btn"
                                      >
                                        Clear
                                      </button>
                                    )}
                                  </div>
                                  {variance != null && (
                                    <p
                                      className={`mt-1 text-xs font-semibold ${variance === 0 ? 'text-slate-600' : variance > 0 ? 'text-amber-600' : 'text-red-600'}`}
                                      data-testid="variance-display"
                                    >
                                      Variance: {variance > 0 ? '+' : ''}{variance}
                                    </p>
                                  )}
                                </div>
                              );
                            })()}
                            <button
                              onClick={() => unpinJob(job.id)}
                              className="w-full text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100"
                              data-testid="unpin-btn"
                            >
                              Unpin
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer hint */}
            {selectedDrawing && (
              <div className="absolute bottom-3 left-3 z-20 bg-white/90 rounded-lg px-3 py-1.5 text-xs text-slate-500 border border-slate-200">
                Scroll to zoom · drag to pan · drag a job here to pin ({pinnedJobs.length} pinned)
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ReferenceDrawing;
